import { useEffect, useMemo } from 'react'
import useTypedSelector from 'interfaces/useTypedSelector'
import { useDispatch } from 'react-redux'
import { setActiveCompanyRelationId, setSalesPersonActiveCompany } from 'reducers/authReducer'
import { getInLocalStorage, setInLocalStorage } from 'helpers/storage/storage'
import { StorefrontCompany, StorefrontCompanyUser } from '@local-types/user/storefrontUser'

const KEY = 'active-company-id'
const SALES_PERSON_KEY = 'sales-person-active-company'

// SalesPersons are not assigned to companies via CompanyUsers rows, so we build a
// synthetic relation that grants full access for whichever company they are acting on.
const synthesizeSalesPersonRelation = (company: StorefrontCompany): StorefrontCompanyUser => ({
	id: company.id,
	canViewPrices: true,
	canSubmitOrders: true,
	canSeeOrderHistory: true,
	company,
	isActive: true,
	role: 'User',
})

export const useActiveCompany = () => {
	const dispatch = useDispatch()
	const { userInfo, activeCompanyRelationId, userInfoState, salesPersonActiveCompany } = useTypedSelector(
		({ auth }) => auth
	)
	const isSalesPerson = !!userInfo?.isSalesPerson
	const valueFromStorage = getInLocalStorage(KEY)

	const activeRelation = useMemo(() => {
		if (isSalesPerson) {
			return salesPersonActiveCompany ? synthesizeSalesPersonRelation(salesPersonActiveCompany) : undefined
		}
		return userInfo?.companyUsers.find(x => x.id === activeCompanyRelationId)
	}, [isSalesPerson, salesPersonActiveCompany, userInfo, activeCompanyRelationId])

	const setRelation = (id: string) => {
		setInLocalStorage(KEY, id)
		dispatch(setActiveCompanyRelationId(id))
	}

	const setSalesPersonCompany = (company: StorefrontCompany) => {
		setInLocalStorage(SALES_PERSON_KEY, JSON.stringify(company))
		dispatch(setSalesPersonActiveCompany(company))
	}

	// Restore a salesPerson's previously selected company from storage
	useEffect(() => {
		if (!isSalesPerson || salesPersonActiveCompany) return
		const stored = getInLocalStorage(SALES_PERSON_KEY)
		if (!stored) return
		try {
			dispatch(setSalesPersonActiveCompany(JSON.parse(stored) as StorefrontCompany))
		} catch (e) {
			// Ignore malformed storage value
		}
	}, [isSalesPerson, salesPersonActiveCompany])

	// Regular users default to their first company connection when none is selected.
	// SalesPersons have no companyUsers, so this logic is skipped for them.
	useEffect(() => {
		if (isSalesPerson) return

		// The user has an active list of relations but no value is set in the store
		const noActiveStorageValue = userInfo?.companyUsers && userInfo.companyUsers.length > 0 && !valueFromStorage
		// The user has an active list of relations and a value set in storage but it is not a part of his current active relations
		// (This can happen if a user's previously active relation has been revoked)
		const noMatchFound =
			userInfo?.companyUsers &&
			valueFromStorage &&
			userInfo.companyUsers.length > 0 &&
			!userInfo.companyUsers.find(x => x.id === valueFromStorage)

		if (noActiveStorageValue || noMatchFound) {
			setRelation(userInfo?.companyUsers[0].id || '')
		} else if (valueFromStorage !== activeCompanyRelationId && valueFromStorage) {
			dispatch(setActiveCompanyRelationId(valueFromStorage))
		}
	}, [userInfo, valueFromStorage, isSalesPerson])

	return { activeRelation, setRelation, setSalesPersonCompany, isSalesPerson, state: userInfoState }
}
